import {
  getPreferredIdForStop,
  Stop,
  TrainIdentifier,
} from "@vygruppen/vy-train-map";
import { InternalMessagesFormField } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { InternalMessageRequest } from "features/CenterContent/RoleContent/InternalMessage/schema/requestSchema";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { getCurrentCalendarDateTime } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/transformers";
import {
  formatTime,
  Stop as LegacyStopId,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import {
  VisibilityForm,
  visibilityFormToRequest,
} from "shared/form/visibilitySchema";

export const toCustomText = (contexts: string[], description: string) => ({
  contexts,
  texts: {
    NOB: { title: "", description }, // must be same language as `internalMessagesResponseToFormHistory`
  },
});

export const internalMessagesFormDefault = (
  options?: formDefaultOptions,
): InternalMessagesFormField => {
  const { uuid = undefined, message = "" } = options ?? {};
  return [
    {
      uuid,
      message,
    },
  ];
};

const mapToLegacyStopId = (stop: Stop): LegacyStopId | undefined => {
  if (!stop.stopIds?.length) return undefined;
  const stopId = getPreferredIdForStop(stop) ?? stop.stopIds[0];

  return { id: stopId.id, name: stopId.name ?? stopId.id };
};

const legacyStopIdExists = (
  legacyStopId: LegacyStopId | undefined,
): legacyStopId is LegacyStopId => legacyStopId !== undefined;

export const toInternalMessageWithEvaluationFormRequest = (
  uuid: string | undefined,
  evaluationForm: { url: string },
  contexts: string[],
  visibility: VisibilityForm,
  incidentId?: string,
  relatedEventUuid?: string,
): InternalMessageRequest => ({
  uuid,
  globalInformationType: "GLOBAL_INTERNAL_INFO",
  incidentIds: incidentId ? [incidentId] : [],
  countryCode: "NO",
  customTexts: [
    toCustomText(contexts, "Tilbakemeldingsskjema til togpersonell"),
  ],
  visibility: visibilityFormToRequest(visibility),
  relevantStops: [],
  evaluationForm,
  relatedEventUuid,
});

export const internalMessagesFormToRequest = (
  form: InternalMessagesFormField,
  visibility: VisibilityForm,
  options: {
    incidentId?: string;
    relevantTrain?: TrainIdentifier;
    relevantStops?: Stop[];
    relatedEventUuid?: string;
    relatedEventPrevUuid?: string;
  } = {},
): InternalMessageRequest | undefined => {
  if (!form) return undefined;
  const latestInternalMessage = form[0];
  if (!latestInternalMessage?.message) return undefined;
  const prevInternalMessage = form[1];
  const messageIsUnchanged =
    latestInternalMessage.message === prevInternalMessage?.message;
  const relatedEventUuidIsChanged =
    options.relatedEventUuid !== options.relatedEventPrevUuid;
  if (messageIsUnchanged && !relatedEventUuidIsChanged) return undefined;
  const { uuid, contexts, message } = latestInternalMessage;
  /**
   * The textarea is disabled until contexts are retrieved,
   * therefore '!contexts' shouldn't be possible
   */
  if (!contexts) {
    log(
      LogLevel.error,
      "internalMessages",
      "Intern melding hadde ingen contexts. Dette skal ikke være mulig",
    );
    return undefined;
  }
  const { incidentId, relevantTrain, relevantStops, relatedEventUuid } =
    options;
  return {
    uuid, // Not sent to backend. Used as pathVariable

    globalInformationType: "GLOBAL_INTERNAL_INFO",
    visibility: visibility
      ? visibilityFormToRequest(visibility)
      : {
          // Is overwritten in `createInternalMessage`
          type: "TIMED",
          fromTime: formatTime(getCurrentCalendarDateTime()),
          toTime: formatTime(getCurrentCalendarDateTime(24)),
        },
    incidentIds: incidentId ? [incidentId] : [],
    countryCode: "NO",
    customTexts: [toCustomText(contexts, message)],
    relevantStops:
      relevantStops?.map(mapToLegacyStopId).filter(legacyStopIdExists) ?? [],
    relevantTrain,
    relatedEventUuid,
  };
};

// Converts each response to form
export const internalMessagesResponseToFormHistory = (
  form: InternalMessageResponse[],
): InternalMessagesFormField =>
  (form ?? []).map(({ originSystem, customTexts, updatedAt }) => ({
    uuid: undefined, // unused for prevMsgs
    message: customTexts[0].texts.NOB?.description ?? "", // must be same language as `toCustomText`
    user: originSystem.user ?? originSystem.name,
    updatedAt,
    contexts: undefined, // unused for prevMsgs
  }));

// Converts each response to form and adds an empty form for editing
export const internalMessagesResponseToForm = (
  form: InternalMessageResponse[],
): InternalMessagesFormField => {
  if (!form || form.length === 0) return internalMessagesFormDefault();
  const latestUuid = form[0].uuid;
  const newMsg = internalMessagesFormDefault({ uuid: latestUuid });
  const prevMsgs = internalMessagesResponseToFormHistory(form);
  newMsg[0].message = prevMsgs[0].message;
  return [...newMsg, ...prevMsgs];
};
type formDefaultOptions = {
  uuid?: string;
  message?: string;
};
